import type { IconNames } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { formatPortsHelper } from 'components/powerFlowShared/helpers/nodePortFormatter';
import {
  FlowNode,
  flowNodeColor,
  InternalPortT,
  NodeT,
  NodeTypeComponent,
  RegisterPortT,
} from 'fable/flowEditor';

import { PowerEmailFlowExtraDataT } from '../PowerEmailFlow';
import { IntentDetectionModal as ActionModal } from '../modals/intentDetection';

export type IntentDetectionNodeRef = {
  id: string;
  nextNodeId: string;
};

export type FlowEmailNodeIntentDetectionPayloadT = {
  defaultNextNodeId: string;
  intentDetections: IntentDetectionNodeRef[];
};

export type ButtonsT = Record<string, ButtonT>;
export type ButtonT = {
  button: string;
  nodeId: string;
  order?: number;
};

export type FlowEmailNodeIntentDetectionTypeT = FlowNodeTypes.INTENT_DETECTION;
export type FlowEmailNodeIntentDetectionT = NodeT<
  FlowEmailNodeIntentDetectionTypeT,
  FlowEmailNodeIntentDetectionPayloadT
>;

const portTranslator = (
  node: FlowEmailNodeIntentDetectionT,
): RegisterPortT[] => {
  const {
    payload: { intentDetections },
  } = node;

  return Object.entries(intentDetections).map(([key, { id, nextNodeId }]) => ({
    id: id,
    type: 'out',
    label: id,
    connection: nextNodeId || uuid(),
  }));
};

export class IntentDetectionNode extends NodeTypeComponent<
  FlowEmailNodeIntentDetectionT,
  PowerEmailFlowExtraDataT
>() {
  static icon: IconNames = 'lightbulb';
  static label = 'Intent detection';
  static color = flowNodeColor.purple;
  static translatePayload = formatPortsHelper(portTranslator);
  static ActionModal = ActionModal;
  static actionTrigger = { type: FlowNodeTypes.INTENT_DETECTION };
  static updateConnection = (
    node: FlowEmailNodeIntentDetectionT,
    port: InternalPortT,
    newConnection: string,
  ): FlowEmailNodeIntentDetectionT => {
    const { payload } = node;
    const intentDetections = { ...payload.intentDetections };

    const intentConnection = intentDetections.find(
      (intent) => intent.id === port.label,
    );

    if (port.label && intentConnection) {
      const newIntentDetection: IntentDetectionNodeRef = {
        id: intentConnection.id,
        nextNodeId: newConnection,
      };
      intentDetections.push(newIntentDetection);
    }

    return {
      ...node,
      payload: {
        ...payload,
        intentDetections,
      },
    };
  };

  @boundMethod
  triggerEdit(): void {
    const { onEdit, node } = this.props;
    onEdit(node, node.nodeType);
  }

  render() {
    const {
      node: {
        payload: { intentDetections },
      },
    } = this.props;
    const optionCount = Object.keys(intentDetections).length;
    const optionText =
      optionCount === 1 ? 'Intent Detection' : 'Intent Detections';

    return (
      <FlowNode
        color={IntentDetectionNode.color}
        icon={IntentDetectionNode.icon}
        label={IntentDetectionNode.label}
        text={
          <span>
            <strong>
              {optionCount} {optionText}
            </strong>
          </span>
        }
        onEdit={this.triggerEdit}
      />
    );
  }
}
